import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { SHOP_SECTION_ID, scrollToSection } from '../utils/scroll';
import { goToShop } from '../utils/shopIntent';
import { pricingConfig } from '../data/pricing';
import { getBundle, priceBundle } from '../utils/pricing';
import { track } from '../utils/analytics';
import type { BundleId } from '../data/pricing';
import { HomeHero } from '../components/home/HomeHero';
import { QuickPick } from '../components/home/QuickPick';
import { ProductReveal } from '../components/home/ProductReveal';
import { MountTeaser } from '../components/home/MountTeaser';
import { PromiseSection } from '../components/home/PromiseSection';
import { ApplicationsStrip } from '../components/home/ApplicationsStrip';
import { ClosingCta } from '../components/home/ClosingCta';
import { ShopTabs } from '../components/shop/ShopTabs';
import { TestimonialsMarquee } from '../components/home/TestimonialsMarquee';
import { AlwaysReady } from '../components/home/AlwaysReady';
import { FeaturedSystems } from '../components/shop/FeaturedSystems';
import { PositionsCalculator } from '../components/shop/PositionsCalculator';
import { GuaranteeStrip } from '../components/shop/GuaranteeStrip';
import { HomeFaq } from '../components/home/HomeFaq';
import { ProductVideo } from '../components/home/ProductVideo';
export function Home() {
  const {
    hash,
    search
  } = useLocation();

  // The homepage is the ad landing page and the main product page — tell Meta which product was viewed.
  useEffect(() => {
    const featured = getBundle(pricingConfig.defaultBundle);
    track('view_content', {
      content_name: 'M1 PRO Lighting Systems',
      content_ids: pricingConfig.bundles.map(b => b.variantId),
      content_type: 'product',
      value: priceBundle(featured).total,
      currency: pricingConfig.currency
    });
  }, []);

  // Arriving via /#shop-systems (SHOP NOW from another page), optionally with ?system=sys8.
  useEffect(() => {
    const target = hash.replace('#', '');
    if (target !== SHOP_SECTION_ID && target !== 'reviews') return;
    const system = new URLSearchParams(search).get('system');
    const intent = system && pricingConfig.bundles.some(b => b.id === system) ? system as BundleId : null;
    const id = window.setTimeout(() => intent ? goToShop(intent) : scrollToSection(target), 80);
    return () => window.clearTimeout(id);
  }, [hash, search]);
  return <>
      <HomeHero data-id="element-1135" data-mp="23dkstis9su61" />
      <QuickPick data-id="element-1136" data-mp="28a1s994x6uyf" />
      <ProductVideo data-id="element-1137" data-mp="28akyvtp4dwx5" />
      <ProductReveal data-id="element-1138" data-mp="2890546amqi0c" />
      <MountTeaser data-id="element-1139" data-mp="289ibvwux4emi" />
      <AlwaysReady data-id="element-1140" data-mp="1tlf1mb9uegrw" />
      <FeaturedSystems data-id="element-1141" data-mp="1tlx61v6ns1aw" />

      <section id={SHOP_SECTION_ID} tabIndex={-1} aria-labelledby="paths-title" className="scroll-mt-[72px] border-t mf-hairline bg-deep focus:outline-none" data-id="element-1142" data-mp="1tkdn1wtmbc5f">
        <div className="mx-auto max-w-site px-5 py-10 md:px-8 md:py-14" data-id="element-1143" data-mp="2m91de6t84rpu">
          <h2 id="paths-title" className="max-w-4xl font-display text-5xl font-semibold uppercase leading-[0.92] md:text-7xl" data-id="element-1144" data-mp="2r1b2jkmilnr2">
            Choose your <span className="text-ice" data-id="element-1145" data-mp="fk999j204vml">MarineForceLED System</span>
          </h2>
          <p className="mt-5 font-display text-lg font-semibold uppercase tracking-[0.08em] text-white md:text-xl" data-id="element-1146" data-mp="alo8niiu89xm">
            From your first lighting position to a complete backup lighting system.
          </p>
          <p className="mt-4 font-display text-base font-semibold uppercase tracking-[0.14em] text-ice" data-id="element-1147" data-mp="kd2cu3lqhxx3">Install once. Swap whenever you need.</p>
          <div className="mt-6 md:mt-8" data-id="element-1148" data-mp="3nt96dhafqes5">
            <ShopTabs data-id="element-1149" data-mp="3nukji7prsyvx" />
          </div>
          <GuaranteeStrip data-id="element-1150" data-mp="3nvlvceem1pep" />
        </div>
      </section>

      <section id="find-system" tabIndex={-1} aria-labelledby="positions-title" className="scroll-mt-[72px] border-t mf-hairline bg-[#091C31] focus:outline-none" data-id="element-1151" data-mp="5opgv6mncf2l">
        <div className="mx-auto max-w-site px-5 py-10 md:px-8 md:py-14" data-id="element-1152" data-mp="1h1i1zykxm59o">
          <h2 id="positions-title" className="max-w-3xl font-display text-5xl font-semibold uppercase leading-[0.92] md:text-6xl" data-id="element-1153" data-mp="1c485y031evcv">
            How many lighting positions <span className="text-ice" data-id="element-1154" data-mp="27fzhfrfc44bk">do you want?</span>
          </h2>
          <p className="mt-5 max-w-xl text-lg text-titanium" data-id="element-1155" data-mp="2cdhvtxc4fbqy">
            Pick your positions and we’ll match the official system — with an optional charged backup set.
          </p>
          <div className="mt-6 md:mt-8" data-id="element-1156" data-mp="2mc71mrqqt5qu">
            <PositionsCalculator data-id="element-1157" data-mp="2m92upmlw7vwh" />
          </div>
        </div>
      </section>

      <TestimonialsMarquee data-id="element-1158" data-mp="2qc7q757k2xbm" />
      <PromiseSection data-id="element-1159" data-mp="2qawmt6i0vrl1" />
      <ApplicationsStrip data-id="element-1160" data-mp="2vbc05y2b0z30" />
      <HomeFaq data-id="element-1161" data-mp="2vaj3jpx27pyl" />
      <ClosingCta data-id="element-1162" data-mp="2va983mt8wqli" />
    </>;
}